/**
 * Farbschwellen fuer eine EINZELNE Score-Komponente (0 bis 100).
 *
 * Eigenes Modul, nicht in `AnimatedBar.tsx`: die Regel wird seit Task 15 an
 * zwei Orten gebraucht (Feed-Check-Aufklapper und Score-Bereich der
 * Werkbank-Karte), und `AnimatedBar.tsx` zieht ueber `getScoreHint` den
 * gesamten i18n-Bootstrap mit. Ein Import nur fuer drei Schwellenwerte haette
 * damit jeden Test, der die Werkbank-Karte rendert, an `src/lib/i18n.ts`
 * gekoppelt.
 *
 * Abgrenzung: das sind die Schwellen fuer KOMPONENTEN. Der Gesamtscore hat
 * eigene Baender (kritisch unter 40, Warnung bis 70), die in den Filterpillen
 * der Werkbank und in `gradeForScore` stecken. Zwei Groessen, zwei Skalen; sie
 * duerfen nicht gegeneinander vereinheitlicht werden.
 */
export function getScoreTextColor(score: number): string {
  // -ink, nicht die nackten Signal-Token. `--destructive`, `--warning` und
  // `--success` sind FLAECHEN-Token: als Schriftfarbe auf `--card` messen sie
  // im hellen Theme 3,78:1 (destructive), 2,13:1 (warning) und 4,24:1
  // (success) und reissen damit AA (4,5:1 fuer normalen Text). Genau dafuer
  // haelt das Repo die -ink-Varianten, und genau diese Verwechslung hat schon
  // die Statuspille und die Wirkungszeile getroffen.
  //
  // Betroffen waren beide Aufrufer: die Gesamtpunktzahl und die
  // Komponentenwerte der Werkbank-Karte (ScorePanel) und die Zahl am
  // Feed-Check-Balken (AnimatedBar). Gepinnt in
  // `src/components/workbench/workbenchContrast.test.ts`.
  if (score < 50) return 'hsl(var(--destructive-ink))';
  if (score < 70) return 'hsl(var(--warning-ink))';
  return 'hsl(var(--success-ink))';
}

/**
 * Balken-Ton fuer eine EINZELNE Score-Komponente im Score-Aufbau der
 * Werkbank-Karte (Design-Audit N5, Durchgang 3).
 *
 * Alle sechs Balken standen bisher uniform in Akzent-Violett -- die Semantik
 * haengt allein an `getScoreTextColor` der Zahl daneben, die Flaeche selbst
 * ist beim Scannen ohne Zahlenlesen blind. Das Mockup faerbt die Balken
 * (`background:var(--success/warning/danger)`) genau deshalb semantisch.
 *
 * EIGENE Schwellen, nicht die von `getScoreTextColor` oben -- die Zahl bleibt
 * unveraendert bei 50/70 (Auftrag: "Zahlenfarbe bleibt wie sie ist"). Der
 * Balken uebernimmt stattdessen 40/70, dieselben Schwellenwerte wie der
 * Quality-Score-Badge (`SCORE_BADGE_THRESHOLDS` in
 * `workbench/scoreBadgeVariant.ts`) -- ein Nutzer liest damit am Balken
 * dieselbe Grenze wie am Badge, statt einer dritten, unabhaengigen Skala.
 * Solide (70-84) und Exzellent (85-100) fallen fuer den Balken zu EINEM
 * Erfolgston zusammen: ein Balken zeigt "schwach / mittel / gut" auf einen
 * Blick, die vierte neutrale Badge-Stufe waere hier Rauschen statt Signal.
 *
 * Reine Tailwind-Token-Klassen (kein Custom-Hex, keine Opacity-Variante): die
 * drei Toene sind dieselben AA-geprueften Custom-Properties, die auch die
 * tonalen Badges tragen (`--destructive`, `--warning`, `--success`), hier
 * deckend statt als Text-auf-hell-Paar -- der AA-Kontrast einer Badge-Flaeche
 * betrifft Text, ein unbeschrifteter Balken hat dieses Problem nicht.
 */
export function scoreBarToneClass(score: number): string {
  if (score < 40) return 'bg-destructive';
  if (score < 70) return 'bg-warning';
  return 'bg-success';
}

/**
 * Ink-Ton fuer die VERLUSTZAHL einer Treiber-Zeile (Report V2 Task 4).
 * Dieselben Schwellen wie scoreBarToneClass (40/70), aber als -ink-Textfarbe:
 * die Zahl steht auf heller Flaeche, die deckenden Flaechen-Tokens fallen
 * dort unter AA (Begruendung im Kopf von index.css zu --success-ink).
 */
export function costInkClass(score: number): string {
  if (score < 40) return 'text-destructive-ink';
  if (score < 70) return 'text-warning-ink';
  return 'text-success-ink';
}

/**
 * Balken-Ton fuer die vereinte Kosten-Liste des Feed-Checks (Task T3,
 * Spec 5.3), aus dem PUNKTEVERLUST einer Zeile, nicht aus ihrem Rohwert.
 *
 * Das ist der Unterschied zu `scoreBarToneClass`/`costInkClass` oben: dort
 * faerbt der ROHWERT der Komponente (40/70), hier faerbt, wie viel die Zeile
 * den Feed KOSTET (5/10 Punkte). Beides kann auseinanderfallen — genau das
 * Paradox, das `scoreBreakdown.ts` im Kopf beschreibt: ein Titel mit 70
 * Punkten Rohwert kostet wegen seines 25-Prozent-Gewichts trotzdem mehr als
 * eine Google-Kategorie mit 35 Punkten Rohwert und nur 10 Prozent Gewicht.
 * Nach Rohwert gefaerbt saehe die Titel-Zeile gruen aus, obwohl sie der
 * teuerste Treiber ist.
 *
 * Die Schwellen 5 und 10 kommen aus Spec 5.3 und sind bewusst dieselbe 5,
 * die die Score-Karte fuer "spuerbarer Verlust" benutzt (`verdictLoss` in
 * `optimization-score.json`/den Aufrufern) — ein Verlust unter 5 Punkten
 * gilt app-weit als nicht spuerbar.
 *
 * `scoreBarToneClass` und `costInkClass` bleiben unveraendert bestehen: die
 * Werkbank-Karte (`ScorePanel`) liest weiterhin den Rohwert einer einzelnen
 * Komponente, dort gibt es keinen "Feed-Verlust" zu faerben.
 */
export function lossToneClass(cost: number): string {
  if (cost >= 10) return 'bg-destructive';
  if (cost >= 5) return 'bg-warning';
  return 'bg-success';
}

/** Ink-Pendant zu `lossToneClass`, fuer die Verlustzahl auf heller Flaeche. */
export function lossInkClass(cost: number): string {
  if (cost >= 10) return 'text-destructive-ink';
  if (cost >= 5) return 'text-warning-ink';
  return 'text-success-ink';
}
